import { permissionEngine } from '@/core/permissions/permissionEngine';
import { useAuth } from '@/core/auth/AuthContext';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, X } from 'lucide-react';
import { AuthButton } from '@/core/permissions/AuthButton';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { InventoryForm } from '../components/InventoryForm';
import { useInventoryEdit } from './hooks/useInventoryEdit';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { InventoryNewSidebar } from '../new/components/InventoryNewSidebar';

export function InventoryEditPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'inventory.items.update');

 const { id = '' } = useParams();
 const navigate = useNavigate();
 const {
 values,
 setValues,
 imagePreview,
 setImagePreview,
 setImageFile,
 stockValue,
 lookups,
 updateItem,
 isSaving,
 isLoading,
 } = useInventoryEdit(id);
 const [confirmOpen, setConfirmOpen] = useState(false);

 const handleSave = async () => {
 try {
 const success = await updateItem();
 if (success) {
 setConfirmOpen(false);
 navigate(`/inventory/items/detail/${id}`);
 }
 } catch (error) {
 console.error('[InventoryEditPage] handleSave error:', error);
 alert('Có lỗi xảy ra khi cập nhật vật tư. Vui lòng kiểm tra lại.');
 }
 };

 if (isLoading) {
 return (
 <div className="flex flex-col gap-4 pb-8">
 <div className="h-20 animate-pulse rounded-md bg-white p-3" />
 <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_340px]">
 <div className="h-[600px] animate-pulse rounded-md bg-white" />
 <div className="h-64 animate-pulse rounded-md bg-white" />
 </div>
 </div>
 );
 }

 return (
 <div className="flex flex-col gap-4 pb-8">
 {/* Header */}
  <PageHeaderNew 
    title="Chỉnh sửa vật tư" 
    subtitle={`${values.name} (${values.code})`} 
    backUrl={`/inventory/items/detail/${id}`} 
  />

 {/* Main Content */}
 <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_340px]">
 <div className="min-w-0">
 <InventoryForm
 values={values}
 stockValue={stockValue}
 lookups={lookups}
 onChange={(key, value) => setValues((current) => ({ ...current, [key]: value }))}
 isEdit
 />
 </div>
 <div className="space-y-4">
 <InventoryNewSidebar
 imagePreview={imagePreview}
 onPickImage={(file) => {
 setImageFile(file);
 setImagePreview(file ? URL.createObjectURL(file) : '');
 }}
 note={values.note}
 onNoteChange={(note) => setValues((current) => ({ ...current, note }))}
 />
 </div>
 </div>

 {/* Actions Footer */}
 <div className="flex items-center justify-end gap-3 rounded-md border border-slate-200 bg-white p-3">
 <button
 type="button"
 onClick={() => navigate(`/inventory/items/detail/${id}`)}
 className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 sm:text-sm text-xs text-slate-700 hover:bg-slate-50 transition-colors"
 >
 Hủy bỏ
 </button>
 <AuthButton
 module="inventory.items"
 action="update"
 behavior="disable"
 type="button"
 onClick={() => setConfirmOpen(true)}
 disabled={isSaving}
 className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-700 px-8 sm:text-sm text-xs text-white hover:bg-emerald-800 transition-colors"
 >
 <Save className="h-4 w-4" />
 Lưu thay đổi
 </AuthButton>
 </div>

 <ConfirmDialog
 isOpen={confirmOpen}
 title="Xác nhận cập nhật"
 message="Các thay đổi sẽ được lưu vào hệ thống. Bạn có muốn tiếp tục?"
 confirmText="Lưu thay đổi"
 isLoading={isSaving}
 onConfirm={handleSave}
 onClose={() => setConfirmOpen(false)}
 />
 </div>
 );
}

export default InventoryEditPage;

